import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { API_URL } from '../config/api';
import { useAuth } from '../context/AuthContext';

interface TeamMember {
  userId: string;
  nickname: string;
  joinedAt: string;
}

interface SubscriptionInfo {
  name: string;
  type: 'Individual' | 'Team';
  isTrial: boolean;
  startDate: string;
  endDate: string;
  status: string;
  teamSize: number;
  daysRemaining: number;
  generationsLimit: number;
  team?: {
    teamId: string;
    name: string;
    membersCount: number;
    members: TeamMember[];
  };
}

const fetchSubscription = async (token: string): Promise<SubscriptionInfo> => {
  const response = await axios.get(`${API_URL}/usersubscription/current`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
  return response.data;
};

export const useSubscription = () => {
  const { token, profile } = useAuth();

  return useQuery({
    queryKey: ['subscription', token],
    queryFn: () => fetchSubscription(token!),
    enabled: !!(token && profile),
    staleTime: 5 * 60 * 1000, // 5 минут
    gcTime: 10 * 60 * 1000, // 10 минут
    retry: 2,
    // Обновляем кеш при фокусе окна (пользователь вернулся на вкладку)
    refetchOnWindowFocus: true,
    // Обновляем кеш при переподключении к интернету
    refetchOnReconnect: true,
  });
};

// Хук для мутаций команды с автоматическим обновлением кеша
export const useTeamMutations = () => {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const addTeamMember = useMutation({
    mutationFn: async (nickname: string) => {
      const response = await axios.post(`${API_URL}/usersubscription/add-team-member`, 
        { nickname }, 
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      return response.data;
    },
    onSuccess: () => {
      // Автоматически обновляем кеш подписки
      queryClient.invalidateQueries({ 
        queryKey: subscriptionQueryKey(token!) 
      });
      console.log('✅ Team member added, cache updated');
    },
    onError: (error: any) => {
      console.error('❌ Failed to add team member:', error);
    }
  });

  const removeTeamMember = useMutation({
    mutationFn: async (memberId: string) => {
      const response = await axios.delete(`${API_URL}/usersubscription/remove-team-member/${memberId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    },
    onSuccess: () => {
      // Автоматически обновляем кеш подписки
      queryClient.invalidateQueries({ 
        queryKey: subscriptionQueryKey(token!) 
      });
      console.log('✅ Team member removed, cache updated');
    },
    onError: (error: any) => {
      console.error('❌ Failed to remove team member:', error);
    }
  });

  return {
    addTeamMember,
    removeTeamMember
  };
};

// Экспортируем ключ запроса для инвалидации
export const subscriptionQueryKey = (token: string) => ['subscription', token]; 